import React from 'react';
import { useBranding } from '../contexts/BrandingContext';

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_LOGO = '/icons/icon-192.png';
const DEFAULT_WORDMARK = '/assets/civilx-tms-logo.png';

/**
 * Renders the company logo.
 *
 * - **Default CivilX TMS** (no tenant logo AND no custom company_name):
 *   renders the full CivilX wordmark image on its own. The wordmark already
 *   contains the "CivilX TMS" text so the adjacent `showName` text is skipped.
 * - **White-labeled tenant** (custom logo and/or company_name):
 *   renders the small tenant logo (icon fallback if not uploaded) followed by
 *   the tenant's company name.
 *
 * `size` sets icon size in Tailwind units (default 8 → w-8 h-8). Only applies
 * to the tenant path; the default wordmark scales by height.
 */
const CompanyLogo = ({ size = 8, showName = true, className = '', testid = 'company-logo' }) => {
  const { branding } = useBranding();
  const hasCustomLogo = !!branding.logo_url;
  const hasCustomName = !!(branding.company_name && branding.company_name.trim());
  const useDefault = !hasCustomLogo && !hasCustomName;

  if (useDefault) {
    // Height scales roughly with the requested size (each size unit ≈ 4px, and
    // the wordmark reads well at ~1.1× the icon size).
    const heightPx = Math.max(24, size * 4 + 4);
    return (
      <div className={`flex items-center ${className}`} data-testid={testid}>
        <img
          src={DEFAULT_WORDMARK}
          alt="CivilX TMS"
          className="w-auto object-contain"
          style={{ height: `${heightPx}px` }}
        />
      </div>
    );
  }

  const sizeClass = `w-${size} h-${size}`;
  const logoSrc = hasCustomLogo ? `${API}${branding.logo_url}` : DEFAULT_LOGO;

  return (
    <div className={`flex items-center gap-2 min-w-0 ${className}`} data-testid={testid}>
      <img
        src={logoSrc}
        alt={branding.company_name || 'CivilX TMS'}
        className={`${sizeClass} object-contain flex-shrink-0`}
        onError={(e) => { e.currentTarget.src = DEFAULT_LOGO; }}
      />
      {showName && hasCustomName && (
        <span className="text-base font-bold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>
          {branding.company_name}
        </span>
      )}
    </div>
  );
};

export default CompanyLogo;
